import React, { useMemo, useRef } from 'react';
// Fix: Use named imports for ReactFlow components and types.
import ReactFlow, {
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  Connection,
  Edge,
  Node,
  OnConnectStartParams,
  NodeChange,
  EdgeChange,
} from 'reactflow';

import { AppNode, SimulationState, WorkflowNodeData, ProcessDefinition } from '../../types';
import Sidebar from '../organisms/Sidebar';
import PropertiesPanel from '../organisms/PropertiesPanel';
import TopBar from '../organisms/TopBar';
import BottomPanel from '../organisms/BottomPanel';
import NodeModal from '../organisms/NodeModal';
import { DeploymentModal } from '../organisms/DeploymentModal';
import CustomNode from '../molecules/CustomNode';
import { useEditorConfig } from '../../contexts/EditorContext';
import { LoadingOverlay } from '../molecules/LoadingOverlay';
import { VariableDefinition } from '../../utils/variableExtraction';
import { CanvasSearch } from '../molecules/CanvasSearch';
import { useSearch } from '../../hooks/useSearch';

export interface WorkflowEditorProps {
    nodes: AppNode[];
    edges: Edge[];
    onNodesChange: (changes: NodeChange[]) => void;
    onEdgesChange: (changes: EdgeChange[]) => void;
    onConnect: (connection: Connection) => void;
    onConnectStart: (_: any, params: OnConnectStartParams) => void;
    onConnectEnd: (event: any) => void;
    onDrop: (event: React.DragEvent) => void;
    onDragOver: (event: React.DragEvent) => void;
    onDragLeave?: () => void;
    
    simulationState: SimulationState;
    onToggleSimulation: () => void;
    onResetSimulation: () => void;

    activeProcessId: string;
    processes: ProcessDefinition[];
    onSelectProcess: (id: string) => void;
    onCreateProcess: () => void;
    onDeleteProcess: (id: string) => void;
    onUpdateProcess: (id: string, data: Partial<ProcessDefinition>) => void;
    onSaveProject: () => void;
    onLoadProject: (file: File) => void;
    
    onSave?: () => void;
    onSaveAndQuit?: () => void;
    onBack?: () => void;
    
    onAutoLayout: () => void;

    onNodeClick: (e: React.MouseEvent, node: Node) => void;
    onNodeDoubleClick: (e: React.MouseEvent, node: Node) => void;
    onNodeContextMenu: (e: React.MouseEvent, node: Node) => void;
    onPaneClick: () => void;
    selectedNode: AppNode | null;
    updateNodeData: (id: string, data: Partial<WorkflowNodeData>) => void;
    
    variableContext?: VariableDefinition[];

    isModalOpen: boolean;
    setIsModalOpen: (open: boolean) => void;
    activeTabId: string;
    setActiveTabId: (id: string) => void;
    isDeployModalOpen: boolean;
    setIsDeployModalOpen: (open: boolean) => void;
    
    isLoading: boolean;
    autoSaveState?: { isSaving: boolean; lastSaved: Date | null };
}

const WorkflowEditor: React.FC<WorkflowEditorProps> = (props) => {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const { nodeTypes: configNodeTypes } = useEditorConfig();

    const nodeTypes = useMemo(() => {
        const types: Record<string, any> = {};
        Object.keys(configNodeTypes).forEach(key => {
            types[key] = CustomNode;
        });
        return types;
    }, [configNodeTypes]);

    const { query, setQuery, matches, activeIndex, nextMatch } = useSearch({
      nodes: props.nodes,
      onSelectNode: (id) => {
        const node = props.nodes.find(n => n.id === id);
        if (node) props.onNodeClick({} as any, node);
      }
    });

    const activeProcessName = props.processes.find(p => p.id === props.activeProcessId)?.name || 'Project';

    return (
        <div className="flex flex-col h-full w-full overflow-hidden bg-slate-50 text-slate-900 font-sans relative">
          <LoadingOverlay isVisible={props.isLoading} message="Processing..." />
          
          <TopBar 
            simulationState={props.simulationState} 
            onToggleSimulation={props.onToggleSimulation} 
            onReset={props.onResetSimulation}
            onExport={props.onSaveProject}
            onImport={props.onLoadProject}
            onSave={props.onSave}
            onSaveAndQuit={props.onSaveAndQuit}
            onBack={props.onBack}
            processes={props.processes}
            activeProcessId={props.activeProcessId}
            onSelectProcess={props.onSelectProcess}
            onCreateProcess={props.onCreateProcess}
            onDeleteProcess={props.onDeleteProcess}
            currentNodes={props.nodes}
            onAutoLayout={props.onAutoLayout}
            autoSaveState={props.autoSaveState}
          />
          
          <div className="flex flex-1 overflow-hidden relative" onDragLeave={props.onDragLeave}>
            <Sidebar />
            
            <div className="flex-1 flex flex-col h-full relative min-w-0">
                <div className="flex-1 relative h-full bg-slate-100" ref={reactFlowWrapper}>
                    <CanvasSearch
                      query={query}
                      onQueryChange={setQuery}
                      matchCount={matches.length}
                      activeIndex={activeIndex}
                      onNext={nextMatch}
                      onClear={() => setQuery('')}
                    />

                    <ReactFlow
                        nodes={props.nodes}
                        edges={props.edges}
                        onNodesChange={props.onNodesChange}
                        onEdgesChange={props.onEdgesChange}
                        onConnect={props.onConnect}
                        onConnectStart={props.onConnectStart}
                        onConnectEnd={props.onConnectEnd}
                        onDrop={props.onDrop}
                        onDragOver={props.onDragOver}
                        onNodeClick={props.onNodeClick}
                        onPaneClick={props.onPaneClick}
                        onNodeDoubleClick={props.onNodeDoubleClick}
                        onNodeContextMenu={props.onNodeContextMenu}
                        nodeTypes={nodeTypes}
                        fitView
                        style={{ width: '100%', height: '100%' }}
                        proOptions={{ hideAttribution: true }}
                        snapToGrid={true}
                        snapGrid={[15, 15]}
                        nodesDraggable={true}
                        nodesConnectable={true}
                        elementsSelectable={true}
                        deleteKeyCode={['Backspace', 'Delete']}
                        defaultEdgeOptions={{
                            interactionWidth: 20,
                            style: { strokeWidth: 2, stroke: '#94a3b8' }
                        }}
                    >
                        <Controls className="bg-white border border-slate-200 shadow-sm !text-slate-600 rounded-lg" />
                        <MiniMap 
                            nodeStrokeColor={(n) => {
                                if (n.data.status === 'error') return '#ef4444';
                                if (n.data.status === 'active') return '#14b8a6'; 
                                if (n.data.status === 'completed') return '#10b981';
                                return '#94a3b8';
                            }}
                            className="!bg-white !border !border-slate-200 !shadow-sm !rounded-xl"
                        />
                        <Background color="#cbd5e1" gap={20} size={1} variant={BackgroundVariant.Dots} />
                    </ReactFlow>
                </div>
                
                <BottomPanel simulationState={props.simulationState} />
            </div>
    
            <PropertiesPanel 
                selectedNode={props.selectedNode}
                activeProcess={props.processes.find(p => p.id === props.activeProcessId)}
                updateNodeData={props.updateNodeData} 
                updateProcessData={props.onUpdateProcess}
                onOpenModal={() => props.setIsModalOpen(true)}
                variableContext={props.variableContext}
            />
          </div>
          
          <NodeModal 
            isOpen={props.isModalOpen} 
            onClose={() => props.setIsModalOpen(false)}
            selectedNode={props.selectedNode}
            updateNodeData={props.updateNodeData}
            variableContext={props.variableContext}
            activeTabId={props.activeTabId}
            setActiveTabId={props.setActiveTabId}
          />

          <DeploymentModal 
            isOpen={props.isDeployModalOpen}
            onClose={() => props.setIsDeployModalOpen(false)}
            projectName={activeProcessName}
          />
        </div>
      );
};

export default WorkflowEditor;